import { LngLat } from '@yandex/ymaps3-types';
import { YMapLocationRequest } from '@yandex/ymaps3-types/imperative/YMap';
import { Feature as ClustererFeature } from '@yandex/ymaps3-types/packages/clusterer';
import { useCallback, useEffect, useState } from 'react';

import {
  COMMON_LOCATION_PARAMS,
  DEFAULT_SELECT_POINT_ZOOM_VALUE,
  LOCATION
} from '../consts';
import { getBounds } from '../helpers/getBounds';
import { PointFeature } from '../types';

/**
 * Хук для управления состоянием карты
 */
export function useMapController(center?: LngLat) {
  const [location, setLocation] = useState<YMapLocationRequest>(
    center
      ? {
          ...LOCATION,
          center
        }
      : LOCATION
  );

  const [selectedPoint, setSelectedPoint] = useState<PointFeature | null>(null);

  useEffect(() => {
    if (center) {
      setLocation((prevLocation) => ({
        ...prevLocation,
        center
      }));
    }
  }, [center]);

  /**
   * Обработчик клика по маркеру
   */
  const handleMarkerClick = useCallback(
    (isMap: boolean) => (feature: PointFeature) => {
      setLocation((prevLocation) => ({
        ...prevLocation,
        bounds: undefined,
        center: [
          feature.geometry.coordinates[0],
          feature.geometry.coordinates[1]
        ],
        zoom: DEFAULT_SELECT_POINT_ZOOM_VALUE,
        ...COMMON_LOCATION_PARAMS
      }));
      setSelectedPoint(feature);

      return { feature, isMap };
    },
    []
  );

  /**
   * Обработчик клика по кластеру
   */
  const handleClusterClick = useCallback((features: ClustererFeature[]) => {
    const bounds = getBounds(
      features.map((feature: ClustererFeature) => [
        feature.geometry.coordinates[0],
        feature.geometry.coordinates[1]
      ])
    );
    setLocation((prevLocation) => ({
      ...prevLocation,
      bounds,
      ...COMMON_LOCATION_PARAMS
    }));
  }, []);

  /**
   * Обработчик выбора геолокации из поиска
   */
  const handleLocationSelect = useCallback((coordinates: [number, number]) => {
    setLocation((prevLocation) => ({
      ...prevLocation,
      center: coordinates,
      zoom: DEFAULT_SELECT_POINT_ZOOM_VALUE,
      ...COMMON_LOCATION_PARAMS
    }));
  }, []);

  return {
    location,
    selectedPoint,
    setLocation,
    setSelectedPoint,
    handleMarkerClick,
    handleClusterClick,
    handleLocationSelect
  };
}
